import { Navigate, useLocation, useParams } from 'react-router-dom';

import { Badge } from '@/components/common/Badge/Badge';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { LoadingState } from '@/components/common/LoadingState/LoadingState';
import { PageBreadcrumb } from '@/components/layout/PageBreadcrumb/PageBreadcrumb';
import { StepHeader } from '@/components/layout/StepHeader/StepHeader';
import { SourceDetails } from '@/components/positions/SourceDetails/SourceDetails';
import { useTopics } from '@/hooks/use-topics';
import { useProposalSource } from '@/hooks/use-proposals';
import { useSeo } from '@/hooks/useSeo';
import { buildPageTitle } from '@/lib/seo';

import styles from '@/styles/positions.module.css';

export function ProposalSourcePage(): JSX.Element {
  const params = useParams<{ topicId: string; candidateId: string }>();
  const location = useLocation();
  const topicsState = useTopics();
  const sourceState = useProposalSource(params.candidateId ?? null, params.topicId ?? null);
  const topic = topicsState.topics.find((entry) => entry.id === params.topicId) ?? null;
  const candidateName = sourceState.candidate?.name ?? params.candidateId ?? 'Candidato';
  const topicName = topic?.label ?? 'Tema';
  const topicDescription = topic
    ? `Conheça as propostas apresentadas para ${topic.label} e consulte as fontes oficiais.`
    : 'Conheça as propostas apresentadas e consulte as fontes oficiais.';
  const isLegacyRoute = location.pathname.startsWith('/posicoes');

  useSeo({
    title: buildPageTitle(`${candidateName} em ${topicName}`),
    description: topicDescription,
    ogTitle: `Veja as propostas de ${candidateName} para ${topicName}`,
    ogDescription: topicDescription,
    canonicalPath:
      params.candidateId && topic
        ? `/match-politico/tema/${topic.id}/candidato/${params.candidateId}`
        : '/match-politico/resumo',
    indexable: !isLegacyRoute,
    image: sourceState.candidate?.photoUrl,
    imageAlt: sourceState.candidate ? `${sourceState.candidate.name} no Match Político` : 'Verde Confirma',
    ogType: 'article',
  });

  if (topicsState.status === 'loading' || sourceState.status === 'loading') {
    return (
      <section className={styles.questionCard}>
        <PageBreadcrumb items={[{ label: 'Match Político', to: '/match-politico' }, { label: 'Resultado', to: '/match-politico/resumo' }, { label: 'Tema' }, { label: 'Fonte' }]} />

        <StepHeader kicker="Fonte" title="Carregando fonte" description="Consultando os dados públicos." />
        <LoadingState title="Carregando fonte" messages={['Consultando documentos', 'Verificando resumos consolidados', 'Preparando detalhes']} />
      </section>
    );
  }

  if (topicsState.status === 'error' || sourceState.status === 'error' || !sourceState.candidate) {
    return (
      <section className={styles.questionCard}>
        <PageBreadcrumb items={[{ label: 'Match Político', to: '/match-politico' }, { label: 'Resultado', to: '/match-politico/resumo' }, { label: 'Tema' }, { label: 'Fonte' }]} />

        <StepHeader kicker="Fonte" title="Não foi possível carregar a fonte" description="Tente novamente em instantes." />
        <EmptyState tone="danger" title="Erro ao carregar a fonte" description={topicsState.error ?? sourceState.error ?? ''} />
      </section>
    );
  }

  if (!topic) {
    return <Navigate to="/match-politico/resumo" replace />;
  }

  return (
    <section className={styles.sourcePage}>
      <PageBreadcrumb
        items={[
          { label: 'Match Político', to: '/match-politico' },
          { label: 'Resultado', to: '/match-politico/resumo' },
          { label: topic.label, to: `/match-politico/tema/${topic.id}` },
          { label: sourceState.candidate.name },
        ]}
      />

      <Badge tone="primary">{topic.label}</Badge>

      <header className={styles.sourcePageHeader}>
        <h1 className={styles.sourcePageTitle}>{sourceState.candidate.name}</h1>
        <p className={styles.sourcePageSubtitle}>Posição sobre {topic.label}</p>
      </header>

      {sourceState.proposal ? <SourceDetails candidate={sourceState.candidate} topic={topic} proposal={sourceState.proposal} /> : null}
    </section>
  );
}
